import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { parseFloatFromString, getDefaultCurrency, formatCurrency, formatPrice, getChangeColorClass } from '../../utils/formatters';
import { usePositionContext } from '../../contexts/PositionContext';
import { getMarketCapInfo } from '../../utils/allocationHelpers';
import Tooltip from '../common/Tooltip';
import TokenSelector from './TokenSelector';
import { PlannerSurface } from './PlannerPresentation';
import { PlannerAddRowButton, PlannerDoneToggle, PlannerDragHandle } from './PlannerTableControls';
import TokenSparkline from '../TokenSparkline';
import DelistedIcon from '../DelistedIcon';
import { tokenRoutePath } from '../../utils/tokenRoutes';

const AllocationTable = ({ 
  allocations, 
  totalAllocation, 
  activeDropdownIndex,
  setActiveDropdownIndex,
  onAddRow,
  onAddCompleteRow,
  onRemoveRow,
  onUpdateAmount,
  onSelectToken,
  onUpdateCompleted,
  onEditRow,
  onEditGroup,
  onEditLocation,
  onReorderRows,
  onAddToken
}) => {
  const amountInputRef = useRef(null);
  const [confirmDeleteIndex, setConfirmDeleteIndex] = useState(null);
  const confirmButtonRef = useRef(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [editingAmountIndex, setEditingAmountIndex] = useState(null);
  const [editingAmountValue, setEditingAmountValue] = useState('');
  
  // Get currency from portfolio settings
  const currency = getDefaultCurrency();
  const { positions } = usePositionContext();

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index);
    // Add a slight delay to allow the drag image to be set
    setTimeout(() => {
      e.target.closest('tr')?.classList.add('opacity-50');
    }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.closest('tr')?.classList.remove('opacity-50');
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const fromIndex = draggedIndex;
    if (fromIndex !== null && fromIndex !== dropIndex && onReorderRows) {
      onReorderRows(fromIndex, dropIndex);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };
  
  const startEditingAmount = (index, value) => {
    setEditingAmountIndex(index);
    setEditingAmountValue(String(value ?? ''));
  };

  const commitEditingAmount = () => {
    if (editingAmountIndex === null) return;
    const parsed = parseFloatFromString(editingAmountValue);
    if (!Number.isNaN(parsed) && parsed >= 0) {
      onUpdateAmount && onUpdateAmount(editingAmountIndex, editingAmountValue === '' ? '0' : editingAmountValue, true);
    }
    setEditingAmountIndex(null);
    setEditingAmountValue('');
  };

  // Inline add row removed; token adding handled by modal

  const toggleDropdown = (index) => {
    if (activeDropdownIndex === index) {
      setActiveDropdownIndex(null);
    } else {
      setActiveDropdownIndex(index);
    }
  };

  const handleAdd = (index) => {
    const item = allocations[index];
    // Only add if token is selected and amount is non-negative
    if ((item.tokenId || item.token?.id) && parseFloatFromString(item.amount) >= 0) {
      onAddRow();
    }
  };

  // Delete confirmation modal controls
  useEffect(() => {
    if (!isDeleteOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setIsDeleteOpen(false);
        setConfirmDeleteIndex(null);
      }
    };
    window.addEventListener('keydown', onKey);
    // Focus confirm button so Enter submits
    setTimeout(() => confirmButtonRef.current?.focus(), 0);
    return () => window.removeEventListener('keydown', onKey);
  }, [isDeleteOpen]);

  // Pending add handlers removed

  const handleKeyPress = (e, index) => {
    if (e.key === 'Enter') {
      handleAdd(index);
    }
  };

  // Pending add keypress removed

  // Separate saved allocations from new ones
  const savedAllocations = allocations.filter(item => 
    (item.tokenId || item.token?.id) && parseFloatFromString(item.amount) >= 0
  );
  
  // Only show items that have a token selected but amount is not valid yet
  // (Don't show completely empty rows - those are handled by pending state)
  const newAllocations = allocations.filter(item => {
    const amount = parseFloatFromString(item.amount);
    return (item.tokenId || item.token?.id) && (Number.isNaN(amount) || amount < 0);
  });

  // If there is nothing to show, render nothing to avoid empty background section
  if (savedAllocations.length === 0 && newAllocations.length === 0) {
    return null;
  }

  // existingCoingeckoApiIds retained only for edit rows
  const existingCoingeckoApiIds = allocations
    .filter(allocation => allocation.token?.coingeckoApiId)
    .map(allocation => allocation.token.coingeckoApiId);

  return (
    <PlannerSurface>
      <div className="space-y-2 md:hidden">
        {savedAllocations.map((item) => {
          const index = allocations.indexOf(item);
          const amountNum = parseFloatFromString(item.amount);
          const percentage = totalAllocation > 0 ? (amountNum / totalAllocation * 100) : 0;
          const token = item.token;
          const pos = positions?.find(p => (token?.id && p.token?.id === token.id) || (token?.coingeckoApiId && p.token?.coingeckoApiId === token.coingeckoApiId));
          const currentRoi = pos ? parseFloatFromString(pos.current?.roi) : 0;
          const currentPnl = pos ? parseFloatFromString(pos.current?.pnl) : 0;
          const currentCost = pos ? parseFloatFromString(pos.investedCost || pos.current?.basisCost) : 0;
          const pendingAmount = item.completed ? 0 : amountNum;
          const newCost = currentCost + pendingAmount;
          const newRoi = newCost > 0 ? (currentPnl / newCost) * 100 : 0;
          const roiDelta = newRoi - currentRoi;
          const roiDeltaRounded = Number(roiDelta.toFixed(2));
          const mcInfo = token ? getMarketCapInfo(token.marketData?.marketCap) : { category: 'Unknown', color: '#6B7280' };
          const priceFromPosition = pos?.token?.marketData?.currentPrice;
          const priceFromToken = token?.marketData?.currentPrice;
          const displayPrice = (priceFromPosition != null && priceFromPosition !== '') ? priceFromPosition : priceFromToken;
          const isCompleted = !!item.completed;

          return (
            <div key={index} className={`rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/40 p-2 text-xs ${isCompleted ? 'opacity-70' : ''}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className={`font-medium ${isCompleted ? 'line-through' : ''}`}>
                    {item.token?.coingeckoApiId || item.token?.coingeckoSlug ? (
                      <Link
                        to={tokenRoutePath(item.token)}
                        className="inline-flex min-w-0 items-center gap-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                        title={item.token?.name}
                      >
                        <span className="truncate">{item.token?.name || 'Unknown Token'}</span>
                        {item.token?.ticker && <span className="shrink-0 text-gray-500 dark:text-gray-400">({item.token.ticker})</span>}
                        {item.token?.isDelisted && <DelistedIcon className="w-3.5 h-3.5" />}
                      </Link>
                    ) : (
                      <span>{item.token?.name || 'Unknown Token'}{item.token?.ticker && <span className="text-gray-500 dark:text-gray-400 ml-1">({item.token.ticker})</span>}</span>
                    )}
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-gray-500 dark:text-gray-400">
                    <span>{displayPrice == null ? '—' : formatPrice(displayPrice, currency)}</span>
                    <span>{mcInfo.category}</span>
                    {pos && <span>{formatCurrency(parseFloatFromString(pos.holdingsValue), currency)}</span>}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => startEditingAmount(index, item.amount)}
                  className="shrink-0 text-right hover:underline"
                  title="Click to edit allocation amount"
                >
                  <div className="font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(amountNum, currency)}</div>
                  <div className="text-[11px] text-gray-500 dark:text-gray-400">{percentage.toFixed(1)}%</div>
                </button>
              </div>

              {editingAmountIndex === index && (
                <div className="mt-2 flex gap-2">
                  <input
                    type="text"
                    value={editingAmountValue}
                    autoFocus
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '' || /^\d*(?:[.,]\d*)?$/.test(v)) setEditingAmountValue(v);
                    }}
                    onBlur={commitEditingAmount}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); commitEditingAmount(); }
                      if (e.key === 'Escape') { setEditingAmountIndex(null); setEditingAmountValue(''); }
                    }}
                    className="w-full rounded border px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              )}

              <div className="mt-2 grid grid-cols-2 gap-2 text-gray-700 dark:text-gray-200">
                <div><span className="text-gray-500 dark:text-gray-400">ROI change</span><br />{roiDeltaRounded === 0 ? <span className="text-gray-400">-</span> : <span className={`${getChangeColorClass(roiDelta)} font-medium`}>{`${roiDeltaRounded >= 0 ? '+' : ''}${roiDeltaRounded.toFixed(2)}%`}</span>}</div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Target</span><br />
                  <button
                    type="button"
                    className="underline decoration-dotted underline-offset-2"
                    onClick={() => onEditLocation && onEditLocation(index)}
                  >
                    {item.targetLocation?.name || 'Set target'}
                  </button>
                </div>
                <div><span className="text-gray-500 dark:text-gray-400">Group</span><br /><button type="button" className="underline decoration-dotted underline-offset-2" onClick={() => onEditGroup && onEditGroup(index)}>{item.token?.group?.name || 'Set group'}</button></div>
                <div><span className="text-gray-500 dark:text-gray-400">Done</span><br /><PlannerDoneToggle completed={isCompleted} onChange={(completed) => onUpdateCompleted && onUpdateCompleted(index, completed)} /></div>
              </div>

              <div className="mt-2 flex items-center gap-3">
                <button onClick={() => onEditRow && onEditRow(index)} className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">Edit</button>
                <button onClick={() => { setConfirmDeleteIndex(index); setIsDeleteOpen(true); }} className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300">Delete</button>
              </div>
            </div>
          );
        })}
        {savedAllocations.length > 0 && onAddToken && (
          <PlannerAddRowButton mobile label="token" onClick={onAddToken} />
        )}
        {savedAllocations.length > 0 && (
          <div className="rounded-md bg-gray-100 dark:bg-gray-700/50 px-3 py-2 text-sm font-semibold dark:text-white">
            Total: {formatCurrency(totalAllocation, currency)}
          </div>
        )}
      </div>
      {/* Allocations Table */}
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full">
          {savedAllocations.length > 0 && (
            <thead className="bg-gray-100 dark:bg-gray-700">
              <tr className="border-b border-gray-200 dark:border-gray-600 text-xs">
                <th className="py-2 px-1 w-6"></th>
                <th className="py-2 px-1 text-left font-medium text-gray-700 dark:text-gray-300">Token</th>
                <th className="py-2 px-1 text-left font-medium text-gray-700 dark:text-gray-300">Price</th>
                <th className="py-2 px-1 text-left font-medium text-gray-700 dark:text-gray-300 w-36">7d chart</th>
                <th className="py-2 px-1 text-left font-medium text-gray-700 dark:text-gray-300">Market Cap</th>
                <th className="py-2 px-1 text-left font-medium text-gray-700 dark:text-gray-300">Current Position</th>
                <th className="py-2 px-1 text-left font-medium text-gray-700 dark:text-gray-300">Allocation ({currency})</th>
                <th className="py-2 px-1 text-left font-medium text-gray-700 dark:text-gray-300">ROI change</th>
                <th className="py-2 px-1 text-left font-medium text-gray-700 dark:text-gray-300">Group</th>
                <th className="py-2 px-1 text-left font-medium text-gray-700 dark:text-gray-300">Location</th>
                <th className="py-2 px-1 text-left font-medium text-gray-700 dark:text-gray-300 w-16">Done</th>
                <th className="py-2 px-1 text-left font-medium text-gray-700 dark:text-gray-300 w-16">Actions</th>
              </tr>
            </thead>
          )}
          <tbody>
            {/* Saved Allocations */}
            {savedAllocations.map((item, origIndex) => {
              const index = allocations.indexOf(item);
              const amountNum = parseFloatFromString(item.amount);
              const percentage = totalAllocation > 0 ? (amountNum / totalAllocation * 100) : 0;
              const token = item.token;
              const pos = positions?.find(p => (token?.id && p.token?.id === token.id) || (token?.coingeckoApiId && p.token?.coingeckoApiId === token.coingeckoApiId));
              const currentRoi = pos ? parseFloatFromString(pos.current?.roi) : 0;
              const currentPnl = pos ? parseFloatFromString(pos.current?.pnl) : 0;
              const currentCost = pos ? parseFloatFromString(pos.investedCost || pos.current?.basisCost) : 0;
              const pendingAmount = item.completed ? 0 : amountNum;
              const newCost = currentCost + pendingAmount;
              const newRoi = newCost > 0 ? (currentPnl / newCost) * 100 : 0;
              const roiDelta = newRoi - currentRoi;
              const roiDeltaRounded = Number(roiDelta.toFixed(2));
              const mcInfo = token ? getMarketCapInfo(token.marketData?.marketCap) : { category: 'Unknown', color: '#6B7280' };
              
              const isCompleted = !!item.completed;
              const isDragOver = dragOverIndex === index && draggedIndex !== index;
              return (
                <tr 
                  key={index} 
                  className={`border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 text-sm ${isCompleted ? 'opacity-70' : ''} ${isDragOver ? 'border-t-2 border-t-blue-500' : ''}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                >
                  <td className="py-2 w-6 cursor-grab active:cursor-grabbing">
                    <PlannerDragHandle label={item.token?.name || 'token'} />
                  </td>
                  <td className="py-2 dark:text-white">
                    <div className={`font-medium ${isCompleted ? 'line-through' : ''}`}>
                      {item.token?.coingeckoApiId || item.token?.coingeckoSlug ? (
                        <Link 
                          to={tokenRoutePath(item.token)}
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 inline-flex items-center gap-1"
                          title={item.token?.name}
                        >
                          {item.token?.name || 'Unknown Token'}{item.token?.ticker && <span className="text-gray-500 dark:text-gray-400 ml-1">({item.token.ticker})</span>}
                          {item.token?.isDelisted && <DelistedIcon className="w-3.5 h-3.5" />}
                        </Link>
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          {item.token?.name || 'Unknown Token'}
                          {item.token?.ticker && <span className="text-gray-500 dark:text-gray-400 ml-1">({item.token.ticker})</span>}
                          {item.token?.isDelisted && <DelistedIcon className="w-3.5 h-3.5" />}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 dark:text-white">
                    {(() => {
                      const priceFromPosition = pos?.token?.marketData?.currentPrice;
                      const priceFromToken = token?.marketData?.currentPrice;
                      const displayPrice = (priceFromPosition != null && priceFromPosition !== '') ? priceFromPosition : priceFromToken;
                      if (displayPrice == null) return <span className="text-gray-400">—</span>;
                      return <span>{formatPrice(displayPrice, currency)}</span>;
                    })()}
                  </td>
                  <td className="py-2">
                    <TokenSparkline 
                      coingeckoId={token?.coingeckoId} 
                      tokenName={token?.name}
                      className="w-32 h-6"
                    />
                  </td>
                  <td className="py-2 dark:text-white">
                    <Tooltip
                      title={(token?.marketData?.marketCap && token.marketData.marketCap !== '0') ? `Market cap: ${token.marketData.marketCap}` : (token?.marketData?.fullyDilutedValuation ? `FDV: ${token.marketData.fullyDilutedValuation}` : 'No data')}
                      position="top" arrow={true} animation="shift" theme="dark" size="small" distance={8}
                    >
                      <span className="border-b border-dotted border-current cursor-help inline-block">{mcInfo.category}</span>
                    </Tooltip>
                  </td>
                  <td className="py-2 dark:text-white">
                    {pos ? (
                      <span>{formatCurrency(parseFloatFromString(pos.holdingsValue), currency)}</span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="py-2 dark:text-white">
                    {editingAmountIndex === index ? (
                      <input
                        type="text"
                        value={editingAmountValue}
                        autoFocus
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '' || /^\d*(?:[.,]\d*)?$/.test(v)) {
                            setEditingAmountValue(v);
                          }
                        }}
                        onBlur={commitEditingAmount}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            commitEditingAmount();
                          } else if (e.key === 'Escape') {
                            setEditingAmountIndex(null);
                            setEditingAmountValue('');
                          }
                        }}
                        className="w-24 px-2 py-1 text-sm border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded"
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => startEditingAmount(index, item.amount)}
                        className="flex items-center gap-1 hover:underline"
                        title="Click to edit allocation amount"
                      >
                        <span>{formatCurrency(amountNum, currency)}</span>
                        <span className="text-gray-500 dark:text-gray-400 text-xs">({percentage.toFixed(1)}%)</span>
                      </button>
                    )}
                  </td>
                  <td className="py-2 dark:text-white">
                    {roiDeltaRounded === 0 ? (
                      <span className="text-gray-400">-</span>
                    ) : (
                      <Tooltip title={`${currentRoi.toFixed(2)}% -> ${newRoi.toFixed(2)}%`} position="top" arrow={true} animation="shift" theme="dark" size="small" distance={8}>
                        <span className={`${getChangeColorClass(roiDelta)} font-medium border-b border-dotted border-current cursor-help`}>
                          {`${roiDeltaRounded >= 0 ? '+' : ''}${roiDeltaRounded.toFixed(2)}%`}
                        </span>
                      </Tooltip>
                    )}
                  </td>
                  <td className="py-2 dark:text-white">
                    <button
                      type="button"
                      className={`text-xs ${item.token?.group?.name ? 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 px-1.5 py-0.5' : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 p-0.5'} rounded`}
                      onClick={() => onEditGroup && onEditGroup(index)}
                      title={item.token?.group ? item.token.group.name : 'Assign to Group'}
                    >
                      {item.token?.group?.name ? (
                        item.token.group.name
                      ) : (
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      )}
                    </button>
                  </td>
                  <td className="py-2 dark:text-white">
                    <button
                      type="button"
                      className={`text-xs ${item.targetLocation?.name ? 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 px-1.5 py-0.5' : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 p-0.5'} rounded`}
                      onClick={() => onEditLocation && onEditLocation(index)}
                      title={item.targetLocation ? item.targetLocation.name : 'Set target location'}
                    >
                      {item.targetLocation?.name ? (
                        item.targetLocation.name
                      ) : (
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      )}
                    </button>
                  </td>
                  <td className="py-2">
                    <PlannerDoneToggle completed={isCompleted} onChange={(completed) => onUpdateCompleted && onUpdateCompleted(index, completed)} />
                  </td>
                  <td className="py-2">
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => onEditRow && onEditRow(index)}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 p-0.5"
                        title="Edit"
                        aria-label="Edit"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                        </svg>
                      </button>
                      <button 
                        onClick={() => { setConfirmDeleteIndex(index); setIsDeleteOpen(true); }}
                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 p-0.5"
                        title="Delete"
                        aria-label="Delete"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            
            {/* Add Token Row - above total */}
            {savedAllocations.length > 0 && onAddToken && (
              <tr className="border-t dark:border-gray-700">
                <td colSpan="12" className="py-2 text-center">
                  <PlannerAddRowButton label="token" onClick={onAddToken} />
                </td>
              </tr>
            )}

            {/* Total Row - only show if there are saved allocations */}
            {savedAllocations.length > 0 && (
              <tr className="font-semibold border-t-2 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/30 text-sm">
                <td className="py-2"></td>
                <td className="py-2 dark:text-white">Total:</td>
                <td className="py-2"></td>
                <td className="py-2"></td>
                <td className="py-2"></td>
                <td className="py-2"></td>
                <td className="py-2 dark:text-white">{formatCurrency(totalAllocation, currency)}</td>
                <td colSpan="5"></td>
              </tr>
            )}
            
            {/* New Allocation Form Rows - existing items being edited */}
            {newAllocations.map((item, newIdx) => {
              const index = allocations.indexOf(item);
              
              // Get all token IDs and coingecko API IDs except the current one
              const otherTokenIds = allocations
                .filter(allocation => allocation.token?.id)
                .map(allocation => allocation.token.id)
                .filter(id => id !== item.token?.id);
              const otherCoingeckoApiIds = existingCoingeckoApiIds.filter(id => id !== item.token?.coingeckoApiId);
              
              return (
                <tr key={index} className="border-t-2 dark:border-gray-600">
                  <td className="py-4"></td>
                  <td className="py-4">
                    <TokenSelector
                      selectedTokenId={item.token?.id}
                      selectedCoingeckoApiId={item.token?.coingeckoApiId}
                      selectedToken={item.token}
                      onSelectToken={(tokenSelection) => {
                        onSelectToken(tokenSelection, index);
                        // Focus amount for existing items
                        setTimeout(() => {
                          if (amountInputRef.current) {
                            amountInputRef.current.focus();
                            amountInputRef.current.select();
                          }
                        }, 0);
                      }}
                      index={index}
                      isActive={activeDropdownIndex === index}
                      toggleActive={toggleDropdown}
                      existingTokenIds={otherTokenIds}
                      existingCoingeckoApiIds={otherCoingeckoApiIds}
                    />
                  </td>
                  <td className="py-4"></td>
                  <td className="py-4"></td>
                  <td className="py-4"></td>
                  <td className="py-4"></td>
                  <td className="py-4">
                    <div className="flex gap-2">
                      <input
                        ref={newIdx === newAllocations.length - 1 ? amountInputRef : null}
                        type="string"
                        value={item.amount || ''}
                        onChange={(e) => onUpdateAmount(index, parseFloatFromString(e.target.value) || 0)}
                        onKeyPress={(e) => handleKeyPress(e, index)}
                        className="flex-1 p-2 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded"
                        min="0"
                        step="1"
                        placeholder="100"
                      />
                      <button
                        onClick={() => handleAdd(index)}
                        disabled={!(item.tokenId || item.token?.id) || parseFloatFromString(item.amount) < 0 || Number.isNaN(parseFloatFromString(item.amount))}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        Add
                      </button>
                    </div>
                  </td>
                  <td colSpan="5"></td>
                </tr>
              );
            })}
            
            {/* Inline add removed; use Add Token button + modal */}
          </tbody>
        </table>
      </div>
      {isDeleteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => { setIsDeleteOpen(false); setConfirmDeleteIndex(null); }} />
          <div className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Delete allocation</h3>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">Are you sure you want to delete this allocation item?</p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setIsDeleteOpen(false); setConfirmDeleteIndex(null); }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                ref={confirmButtonRef}
                onClick={() => { if (confirmDeleteIndex !== null) { onRemoveRow(confirmDeleteIndex); } setIsDeleteOpen(false); setConfirmDeleteIndex(null); }}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </PlannerSurface>
  );
};

export default AllocationTable; 